import React, { createContext, useContext, useEffect, useState } from 'react';
import { Tenant, TenantConfig } from '@bst-stellar/shared';

interface TenantContextType {
    tenant: Tenant | null;
    firebaseConfig: any | null;
    loading: boolean;
    error: string | null;
    refreshTenant: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

const TENANT_CACHE_KEY = 'stellar_tenant_cache';
const CONFIG_CACHE_KEY = 'stellar_config_cache';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

interface CachedData<T> {
    data: T;
    timestamp: number;
    host: string;
    path: string;
}

function getCachedTenant(): Tenant | null {
    try {
        const cached = localStorage.getItem(TENANT_CACHE_KEY);
        if (!cached) return null;

        const parsed: CachedData<Tenant> = JSON.parse(cached);
        const host = window.location.hostname;
        const pathSlug = window.location.pathname.split('/')[1] || '';

        // Check if cache is valid (same host/path prefix and not expired)
        if (
            parsed.host === host &&
            parsed.path === pathSlug &&
            Date.now() - parsed.timestamp < CACHE_DURATION_MS
        ) {
            return parsed.data;
        }
        return null;
    } catch {
        return null;
    }
}

function getCachedConfig(): any | null {
    try {
        const cached = localStorage.getItem(CONFIG_CACHE_KEY);
        if (!cached) return null;

        const parsed: CachedData<any> = JSON.parse(cached);
        const host = window.location.hostname;
        const pathSlug = window.location.pathname.split('/')[1] || '';

        if (
            parsed.host === host &&
            parsed.path === pathSlug &&
            Date.now() - parsed.timestamp < CACHE_DURATION_MS
        ) {
            return parsed.data;
        }
        return null;
    } catch {
        return null;
    }
}

function setCachedTenant(tenant: Tenant) {
    try {
        const cacheData: CachedData<Tenant> = {
            data: tenant,
            timestamp: Date.now(),
            host: window.location.hostname,
            path: window.location.pathname.split('/')[1] || '',
        };
        localStorage.setItem(TENANT_CACHE_KEY, JSON.stringify(cacheData));
    } catch { /* ignore storage errors */ }
}

function setCachedConfig(config: any) {
    try {
        const cacheData: CachedData<any> = {
            data: config,
            timestamp: Date.now(),
            host: window.location.hostname,
            path: window.location.pathname.split('/')[1] || '',
        };
        localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(cacheData));
    } catch { /* ignore storage errors */ }
}

export function TenantProvider({ children }: { children: React.ReactNode }) {
    // Initialize from cache for instant load
    const [tenant, setTenant] = useState<Tenant | null>(() => getCachedTenant());
    const [firebaseConfig, setFirebaseConfig] = useState<any | null>(() => getCachedConfig());
    const [loading, setLoading] = useState(() => !getCachedTenant());
    const [error, setError] = useState<string | null>(null);

    // Apply cached theme immediately
    useEffect(() => {
        const cachedTenant = getCachedTenant();
        if (cachedTenant) {
            applyTheme(cachedTenant);
        }
    }, []);

    useEffect(() => {
        async function discoverTenant() {
            try {
                // Check if we're in demo mode (no backend)
                const apiUrl = import.meta.env.VITE_API_URL;

                if (!apiUrl || apiUrl === 'http://localhost:3001/api') {
                    // Demo mode - use mock data
                    console.log('Running in demo mode with mock tenant data');
                    const mockTenant: Tenant = {
                        id: 'demo-tenant-id',
                        slug: 'demo',
                        name: 'Demo Company',
                        primaryColor: undefined,
                        secondaryColor: '#8b5cf6',
                        logoUrl: undefined,
                        routingMode: 'path' as any,
                        customDomain: undefined,
                        firebaseProjectId: 'demo-project',
                        createdAt: new Date(),
                        updatedAt: new Date(),
                    };

                    setTenant(mockTenant);
                    applyTheme(mockTenant);
                    setLoading(false);
                    return;
                }

                const host = window.location.hostname;
                const path = window.location.pathname;

                // Call tenant discovery API
                const response = await fetch(
                    `${apiUrl}/tenant/discover?host=${host}&path=${path}`
                );

                if (!response.ok) {
                    throw new Error('Tenant not found');
                }

                const data = await response.json();
                setTenant(data.tenant);
                setCachedTenant(data.tenant);

                // Fetch tenant config
                const configResponse = await fetch(
                    `${apiUrl}/tenant/${data.tenant.slug}/config`
                );

                if (configResponse.ok) {
                    const configData: TenantConfig = await configResponse.json();
                    setFirebaseConfig(configData.firebaseConfig);
                    setCachedConfig(configData.firebaseConfig);

                    // Apply theme
                    applyTheme(configData.tenant);
                }
            } catch (err: any) {
                console.error('Tenant discovery error:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }

        discoverTenant();
    }, []);

    function applyTheme(tenant: Tenant) {
        if (tenant.primaryColor) {
            document.documentElement.style.setProperty('--color-primary', tenant.primaryColor);
        }
        if (tenant.secondaryColor) {
            document.documentElement.style.setProperty('--color-secondary', tenant.secondaryColor);
        }
    }

    async function refreshTenant() {
        const apiUrl = import.meta.env.VITE_API_URL;
        if (!apiUrl || apiUrl === 'http://localhost:3001/api') return;

        try {
            const host = window.location.hostname;
            const path = window.location.pathname;
            const response = await fetch(`${apiUrl}/tenant/discover?host=${host}&path=${path}`);
            if (response.ok) {
                const data = await response.json();
                setTenant(data.tenant);
                setCachedTenant(data.tenant);
                applyTheme(data.tenant);
            }
        } catch (err) {
            console.error('Failed to refresh tenant:', err);
        }
    }

    return (
        <TenantContext.Provider value={{ tenant, firebaseConfig, loading, error, refreshTenant }}>
            {children}
        </TenantContext.Provider>
    );
}

export function useTenant() {
    const context = useContext(TenantContext);
    if (context === undefined) {
        throw new Error('useTenant must be used within a TenantProvider');
    }
    return context;
}

